import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../constants';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: string[];
}

class ApiService {
  private client: AxiosInstance;
  private authToken: string | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        if (this.authToken) {
          config.headers.Authorization = `Bearer ${this.authToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid — clear tokens but let React handle redirect
          this.clearAuthToken();
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
        }
        return Promise.reject(error);
      }
    );
  }

  setAuthToken(token: string) {
    this.authToken = token;
  }

  clearAuthToken() {
    this.authToken = null;
  }

  private async request<T>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.request<T>(config);
      const payload = response.data as any;

      // SPA HTML fallbacks / wrong base URLs often return a string body with HTTP 200.
      // Never treat that as a successful API payload.
      if (typeof payload === 'string') {
        const trimmed = payload.trim();
        if (trimmed.startsWith('<!doctype') || trimmed.startsWith('<html') || trimmed.startsWith('<')) {
          return {
            success: false,
            message: 'API returned HTML instead of JSON. Check API_BASE_URL ends with /api.',
          };
        }
        try {
          const parsed = JSON.parse(payload);
          if (parsed && typeof parsed === 'object' && 'success' in parsed) {
            return parsed as ApiResponse<T>;
          }
          return { success: true, data: parsed as T };
        } catch {
          return { success: false, message: 'API returned a non-JSON response.' };
        }
      }

      // Handle different response formats from backend
      if (payload && typeof payload === 'object') {
        // If backend returns wrapped response
        if ('success' in payload) {
          return payload as ApiResponse<T>;
        }
        // If backend returns direct data
        return {
          success: true,
          data: payload as T,
        };
      }

      return {
        success: false,
        message: 'API returned an empty or unsupported response.',
      };
    } catch (error: any) {
      console.error('API Request Error:', error);

      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data === 'string' && (data.trim().startsWith('<!doctype') || data.trim().startsWith('<html'))) {
          return {
            success: false,
            message: 'API returned HTML instead of JSON. Check API_BASE_URL ends with /api.',
          };
        }
        return {
          success: false,
          message: data.message || data.error || 'Request failed',
          errors: data.errors,
        };
      }

      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: 'Network error. Please check your connection and try again.',
        };
      }

      return {
        success: false,
        message: error.message || 'An unexpected error occurred.',
      };
    }
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'PATCH', url, data });
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  // File upload helper
  async uploadFile<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }

  // Multiple file upload helper
  async uploadFiles<T>(
    url: string, 
    files: File[], 
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`files`, file);
    });

    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }
}

// Create and export singleton instance
export const apiService = new ApiService();

// Export class for testing or custom instances
export { ApiService };
